import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  PencilIcon, 
  TrashIcon, 
  UserIcon, 
  CalendarIcon, 
  TagIcon,
  ClockIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';

const TaskCard = ({ 
  task, 
  onEdit, 
  onDelete, 
  onStatusUpdate, 
  canEdit, 
  canDelete,
  getPriorityColor,
  getCategoryColor,
  formatDateToIST,
  isDraggable = true,
  status
}) => {
  // Debug logging to see what's in the task object
  console.log('🔍 TaskCard render:', { task, status, taskStatus: task?.status });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: {
      type: 'task',
      task,
      status
    },
    disabled: !isDraggable
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Use the status prop if task.status is undefined
  const currentStatus = task?.status || status || 'todo';
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && currentStatus !== 'done';

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-gray-700 text-gray-300 border border-gray-600';
      case 'in_progress': return 'bg-blue-900/30 text-blue-300 border border-blue-700';
      case 'review': return 'bg-yellow-900/30 text-yellow-300 border border-yellow-700';
      case 'done': return 'bg-green-900/30 text-green-300 border border-green-700';
      default: return 'bg-gray-700 text-gray-300 border border-gray-600';
    }
  };

  const handleDragStart = (e) => {
    if (isDraggable) {
      e.dataTransfer.setData('text/plain', task.id);
      e.dataTransfer.effectAllowed = 'move';
      console.log('🔍 HTML5 drag started for task:', task.id);
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      className={`group relative overflow-hidden bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-700/50 p-4 hover:shadow-xl hover:bg-gray-700/80 transition-all duration-300 hover:scale-105 cursor-grab active:cursor-grabbing ${isDragging ? 'z-50' : ''}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <h4 className="text-sm font-medium text-white line-clamp-2 flex-1 mr-2">
            {task.title}
          </h4>
          <div className="flex items-center space-x-1">
            {canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(task);
                }}
                className="p-1 text-gray-400 hover:text-blue-400 rounded hover:bg-gray-700 transition-colors duration-300"
                title="Edit task"
              >
                <PencilIcon className="h-3 w-3" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task.id);
                }}
                className="p-1 text-gray-400 hover:text-red-400 rounded hover:bg-gray-700 transition-colors duration-300"
                title="Delete task"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Description */}
        {task.description && (
          <p className="text-xs text-gray-300 mb-3 line-clamp-2">
            {task.description}
          </p>
        )}

        {/* Tags */}
        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {task.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600"
              >
                <TagIcon className="h-3 w-3 mr-1" />
                {tag}
              </span>
            ))}
            {task.tags.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600">
                +{task.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Priority and Category */}
        <div className="flex items-center space-x-2 mb-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
            {task.priority}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(task.category)}`}>
            {task.category}
          </span>
        </div>

        {/* Current Status Display */}
        <div className="mb-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(currentStatus)}`}>
            {currentStatus.replace('_', ' ')}
          </span>
        </div>

        {/* Task Details */}
        <div className="space-y-2 text-xs text-gray-400">
          {/* Assigned To */}
          {task.assigned_to_name && (
            <div className="flex items-center">
              <UserIcon className="h-3 w-3 mr-1" />
              <span className="truncate">Assigned to: {task.assigned_to_name}</span>
            </div>
          )}

          {/* Due Date */}
          {task.due_date && (
            <div className={`flex items-center ${isOverdue ? 'text-red-400' : ''}`}>
              <CalendarIcon className="h-3 w-3 mr-1" />
              <span className="truncate">
                {formatDateToIST(task.due_date)}
                {isOverdue && ' (Overdue)'}
              </span>
            </div>
          )}

          {/* Estimated Hours */}
          {task.estimated_hours && (
            <div className="flex items-center">
              <ClockIcon className="h-3 w-3 mr-1" />
              <span>{task.estimated_hours}h</span>
            </div>
          )}

          {/* Created By */}
          <div className="flex items-center">
            <span className="truncate">Created by: {task.created_by_name}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-gray-600 flex items-center justify-between text-xs text-gray-500">
          <span>
            {new Date(task.created_at).toLocaleDateString()}
          </span>
          {task.comments && task.comments.length > 0 && (
            <div className="flex items-center">
              <ChatBubbleLeftIcon className="h-3 w-3 mr-1" />
              <span>{task.comments.length}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskCard; 